import simbApi from ".";
import {
  SUBITEM_DISCREPANCY_SCAN_PARENT,
  SUBITEM_DISCREPANCY_PARENTS,
  SUBITEM_DISCREPANCY_SCAN_START,
  SUBITEM_DISCREPANCY_SCAN_STATUS,
  SUBITEM_RESYNC_COLUMN,
  SUBITEM_RESYNC_SUBITEM,
  SUBITEM_RESYNC_NAME,
  SUBITEM_RESYNC_PARENT_NAME,
  SUBITEM_CREATE_MISSING,
  SUBITEM_CONNECT,
  SUBITEM_UNLINK,
  SUBITEM_BULK_SCAN,
  SUBITEM_BULK_STATUS,
  SUBITEM_BULK_DRIFT,
  SUBITEM_BULK_CONFIRM,
  SUBITEM_BULK_ACTIVE,
} from "../../data/constants/endpoints";
import logger from "../../lib/logger";
import {
  SubitemScanResponse,
  SubitemScanRow,
} from "../../components/Resynchronization/__fixtures__/discrepancyFixtures";
import { BulkDriftResponse, BulkScanStatusResponse,
  BulkActiveJobResponse,
} from "../../hooks/api/subitemBulk.types";

// Every function here mirrors fetchItemDiscrepancy.ts's shape exactly: simbApi.post with a
// Bearer header, throw on response?.data?.error, return response?.data?.data, and a catch
// that logs and re-throws so SWR (and any caller awaiting a write) sees the rejection.

// BOTH scan functions below are annotated `Promise<SubitemScanResponse>` deliberately. They
// return `response?.data?.data`, which is `any`, so an unannotated return type made every
// consumer's assumption about the response shape invisible to tsc — that is exactly how the
// deferred-parent scan shipped treating this envelope as a bare parent and silently no-op'ing.
// The annotations are load-bearing: the next drift in the engine's response shape must be a
// compile error here, not a feature that quietly stops working.

/**
 * How often to ask whether a scan has finished.
 *
 * A compromise, not a tuned number. Too long and a fast scan — the overwhelming majority, and
 * faster still now the engine fetches boards concurrently — feels slower than the synchronous
 * call it replaced. Too short and a slow board is polled a hundred times for one answer.
 */
const SCAN_POLL_INTERVAL_MS = 1000;

/**
 * A first poll sooner than the interval, because most scans are already done by now and waiting a
 * full second to discover that is a second added to every single scan.
 */
const SCAN_FIRST_POLL_MS = 300;

/**
 * When to stop asking. Well beyond the slowest scan ever observed (48s, before concurrency) and
 * comfortably inside the engine's 300s record TTL, so this deadline is always reached before the
 * record it is polling disappears — a poll that outlived the record would report "start a new
 * one" for a scan that had in fact succeeded.
 */
const SCAN_POLL_TIMEOUT_MS = 120_000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Scans every parent in `parentSyncIds` for subitem discrepancies. Backs useSubitemDiscrepancy.
 *
 * ASYNCHRONOUS UNDERNEATH, with the same signature and the same resolved value as when it was one
 * POST. That is deliberate: the caller is an SWR fetcher, and everything around it — the sorted
 * cache key, the deferred-parent merge, the retry affordance — is about the RESULT, not about how
 * many requests produced it. Changing the shape here would have rewritten all of that for no gain.
 *
 * WHY IT CHANGED. The old single POST sat behind simb-backend's 30s proxy timeout, so a scan
 * slower than that returned 504 and the tab reported "the scan failed" — while the engine had
 * completed normally and the data was fine. Production, account 9013250: scans of 47-48s, failing
 * roughly two attempts in three. Nothing was wrong with the scan except how long it took, and no
 * amount of retrying fixes that.
 *
 * Errors still THROW, so SWR's `error` branch and the existing Retry button keep working
 * unchanged for the failures that are real.
 */
export const scanSubitemDiscrepancies = async (
  access_token: string,
  currentBoard: string,
  parentSyncIds: string[],
): Promise<SubitemScanResponse> => {
  try {
    const started = await simbApi.post(
      SUBITEM_DISCREPANCY_SCAN_START,
      { currentBoard, parentSyncIds },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (started?.data?.error) {
      throw new Error(started?.data?.error);
    }

    const jobId = started?.data?.data?.jobId;
    if (!jobId) {
      throw new Error("The scan could not be started.");
    }

    const deadline = Date.now() + SCAN_POLL_TIMEOUT_MS;
    await wait(SCAN_FIRST_POLL_MS);

    for (;;) {
      const polled = await simbApi.post(
        SUBITEM_DISCREPANCY_SCAN_STATUS,
        { jobId },
        { headers: { Authorization: `Bearer ${access_token}` } },
      );

      if (polled?.data?.error) {
        throw new Error(polled?.data?.error);
      }

      const { state, result, error } = polled?.data?.data ?? {};

      if (state === "done") return result as SubitemScanResponse;
      // The engine records a failed scan as `failed` rather than as a done job carrying an error,
      // precisely so this branch cannot be mistaken for an empty result.
      if (state === "failed") {
        throw new Error(error || "The scan did not complete.");
      }

      if (Date.now() >= deadline) {
        // Distinct wording from the engine's own failures on purpose: the scan may well still be
        // running, and telling someone it FAILED when it did not is how the original 504 misled
        // this customer for a week.
        throw new Error("The scan is taking longer than expected. Please try again.");
      }

      await wait(SCAN_POLL_INTERVAL_MS);
    }
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Scans a single deferred/unverified parent that the bulk scan skipped over. Returns the same
 * `{ parents: [...] }` envelope the bulk scan does — with exactly one entry — NOT a bare
 * parent: the engine's `scanParent` is `scan({ parentSyncIds: [body.parentSyncId] })`
 * (simb-integration-ms/src/api/subitem-discrepancy/subitem-discrepancy.service.ts:336-343),
 * whose only success exits are `setData({ parents: [] })` and `setData({ parents })`, and
 * simb-backend's `sync.controller.ts` proxies that body verbatim.
 */
export const scanSubitemDiscrepancyParent = async (
  access_token: string,
  currentBoard: string,
  parentSyncId: string,
): Promise<SubitemScanResponse> => {
  try {
    const response = await simbApi.post(
      SUBITEM_DISCREPANCY_SCAN_PARENT,
      { currentBoard, parentSyncId },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Every parent sync id on `currentBoard`, in the order the Items list shows them (by item name).
 * Backs useSubitemDiscrepancyWalk.
 *
 * Ids ONLY — no names, no verdicts. The scan supplies a parent's live Monday name, and the
 * setup-time snapshot in the database must not reach the UI (see ParentRow's note on names).
 *
 * WHY A LISTING OF ITS OWN. The Subitems tab's Discrepancies filter can only be answered by
 * scanning every parent, and until this existed the tab had no parent list that did not depend
 * on the Items tab's fetch mode — one server page in All mode, the accumulated set in
 * Discrepancies mode. Reusing `/simb/board-view-items` with a huge `limit` was considered and
 * rejected: that route launches item-discrepancy jobs for every stale parent as a side effect.
 */
export const listSubitemDiscrepancyParents = async (
  access_token: string,
  currentBoard: string,
): Promise<{ parentSyncIds: string[] }> => {
  try {
    const response = await simbApi.post(
      SUBITEM_DISCREPANCY_PARENTS,
      { currentBoard },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Starts a board-wide bulk scan. Returns the job id to poll.
 *
 * A 409 means a bulk job is already running for this account — an ordinary,
 * expected outcome rather than an error, so the caller must be able to tell it
 * apart. Axios puts the status on `error.response.status`, and this function
 * re-throws the axios error unchanged so that survives.
 */
export const startBulkScan = async (
  access_token: string,
  currentBoard: string,
): Promise<{ jobId: string }> => {
  try {
    const response = await simbApi.post(
      SUBITEM_BULK_SCAN,
      { currentBoard },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Polls one bulk scan job.
 *
 * The response never carries column values — the integration service strips
 * `writes` and the token before answering — so this is safe to hold in client
 * state and to log.
 */
export const getBulkScanStatus = async (
  access_token: string,
  jobId: string,
): Promise<BulkScanStatusResponse> => {
  try {
    const response = await simbApi.post(
      SUBITEM_BULK_STATUS,
      { jobId },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * The account's running or most recent bulk job, or `null` when there is none.
 *
 * The question a freshly loaded view asks BEFORE offering a scan. Monday reloads this view's
 * iframe on every navigation back into it, and a view that holds its job id only in memory came
 * back knowing nothing — so it offered "Scan for issues" while the user's own 21-minute scan was
 * still running, and the 409 that produced was read as "something is wrong" and retried eight
 * times in one session (account 9013250, 2026-09-24). Same values discipline as the status route:
 * the answer never carries column values or the token.
 */
export const getBulkActiveJob = async (access_token: string): Promise<BulkActiveJobResponse> => {
  try {
    const response = await simbApi.post(
      SUBITEM_BULK_ACTIVE,
      {},
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * One page of WHAT a finished bulk scan will change — entity, column, and target boards.
 *
 * A separate route from `getBulkScanStatus`, not a richer status. The write plan is the only
 * values-bearing thing the job holds; upstream keeps it under its own Redis key precisely so it
 * does not ride along on a route polled every few seconds, and folding this in would undo that.
 *
 * The response carries NO column values — `DriftRow` has no field one could occupy. A caller
 * wanting the actual before/after opens that parent in the grid, where `/scan-parent` already
 * serves values on the existing ephemeral path.
 */
export const getBulkDrift = async (
  access_token: string,
  jobId: string,
  cursor = 0,
  limit?: number,
): Promise<BulkDriftResponse> => {
  try {
    const response = await simbApi.post(
      SUBITEM_BULK_DRIFT,
      { jobId, cursor, ...(limit ? { limit } : {}) },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Confirms a finished bulk scan's summary and triggers the (irreversible) bulk write. Takes only
 * the job id — not `currentBoard` — because the job itself already carries the board and the
 * account scoping is done by the auth token, same as `getBulkScanStatus`.
 *
 * On success the body's `status` is one of `"writing"` (the write started), `"busy"` (another
 * confirm/write is already in flight for this job), or `"expired"` (the job's TTL elapsed between
 * the scan finishing and this call landing). A non-confirmable job status answers 409, and a job
 * belonging to another account answers 404 — both arrive as thrown axios errors, exactly like
 * `startBulkScan`'s 409, so the caller maps them the same way it already maps that one.
 */
export const confirmBulkResync = async (
  access_token: string,
  jobId: string,
): Promise<{ status: "writing" | "busy" | "expired" }> => {
  try {
    const response = await simbApi.post(
      SUBITEM_BULK_CONFIRM,
      { jobId },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

// --- Write endpoints ---
//
// Body shapes below are pinned to what simb-integration-ms's
// src/api/subitem-discrepancy/subitem-resync.controller.ts (lines 116-240) actually
// destructures off req.body — not to the *Request interfaces' doc comments, which list a
// `token` field the controller never reads from the body (it reads `req.body?.user?.token`,
// which simb-backend's proxy injects itself, in `sync.controller.ts`'s
// `proxySubitemDiscrepancyWrite` — spread body, then set `user`, in that order, so a client
// can't inject one). Diff against that controller file, not this comment, if the contract moves.
//
// Every write route needs `parentSyncId`: it selects the write DESTINATION (which mirrors of
// `subitemSyncId` the service is authorized to touch), while `subitemSyncId` selects the
// CONTENT. Omitting it doesn't just drop a field — it fails the authorization check that makes
// the endpoint safe. None of the write routes read `currentBoard`; it is not sent.

/**
 * Resyncs a single drifting column on a single subitem across its linked boards. `row` is the
 * clicked SubitemScanRow sent back verbatim — for a status/dropdown row this must carry
 * `labelsByBoard`, or the controller rejects with LABELS_REQUIRED_MESSAGE.
 */
export const resyncSubitemColumn = async (
  access_token: string,
  parentSyncId: string,
  subitemSyncId: string,
  columnKey: string,
  row: SubitemScanRow,
  sourceBoardId?: string,
) => {
  try {
    const response = await simbApi.post(
      SUBITEM_RESYNC_COLUMN,
      {
        parentSyncId,
        subitemSyncId,
        columnKey,
        row,
        ...(sourceBoardId ? { sourceBoardId } : {}),
      },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Resyncs every drifting COLUMN on a single subitem across its linked boards. `rows` is not
 * pre-filtered for `ok` rows — the controller filters those itself — but it must NOT include the
 * synthetic `__name__` row. This endpoint has no name path at all: that row's
 * `columnIdsByBoard` is empty by design (subitem-classifier.ts:118-125), so `buildColumnWrites`
 * (subitem-resync.service.ts:717) iterates zero keys and the row is discarded without even a
 * `failed` entry — a silent no-op the service's own comment at :678 acknowledges. Send the name
 * to `resyncSubitemName` instead; SubitemResyncModal's bulk confirm dispatches both.
 */
export const resyncSubitem = async (
  access_token: string,
  parentSyncId: string,
  subitemSyncId: string,
  rows: SubitemScanRow[],
  sourceBoardId?: string,
) => {
  try {
    const response = await simbApi.post(
      SUBITEM_RESYNC_SUBITEM,
      {
        parentSyncId,
        subitemSyncId,
        rows,
        ...(sourceBoardId ? { sourceBoardId } : {}),
      },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Resyncs a subitem's name across its linked boards. `namesByBoard` is derived from the
 * `__name__` row's `valuesByBoard` — note the field is `name`, not `text`.
 */
export const resyncSubitemName = async (
  access_token: string,
  parentSyncId: string,
  subitemSyncId: string,
  namesByBoard: Record<string, { name: string; updatedAt: string }>,
  sourceBoardId?: string,
) => {
  try {
    const response = await simbApi.post(
      SUBITEM_RESYNC_NAME,
      {
        parentSyncId,
        subitemSyncId,
        namesByBoard,
        ...(sourceBoardId ? { sourceBoardId } : {}),
      },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Creates a missing subitem on `targetBoardIds`. `parentSyncId` selects the write
 * destination — the service refuses unless every mirror of `subitemSyncId` hangs off a parent
 * item in that group.
 */
export const createMissingSubitem = async (
  access_token: string,
  parentSyncId: string,
  subitemSyncId: string,
  targetBoardIds: string[],
) => {
  try {
    const response = await simbApi.post(
      SUBITEM_CREATE_MISSING,
      { parentSyncId, subitemSyncId, targetBoardIds },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Connects an `unlinked_subitem` — a Monday subitem under a synced parent that SIMB has no
 * record of — into the sync. The ONE write that sends a Monday item id instead of a sync id:
 * an unlinked subitem has no sync id by definition. The server re-derives authorization from
 * it (reads the subitem with the caller's token, requires its parent to be in `parentSyncId`)
 * rather than trusting it. Mints a new sync group, links the subitem, and creates its copies
 * on the parent group's other boards.
 */
export const connectSubitem = async (access_token: string, parentSyncId: string, subitemId: string) => {
  try {
    const response = await simbApi.post(
      SUBITEM_CONNECT,
      { parentSyncId, subitemId },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Unlinks a subitem (orphaned record state) from the sync. `boardIds` is a
 * narrowing filter — omitting it widens the delete to every mirror the server verified absent,
 * rather than only the named boards.
 */
export const unlinkSubitem = async (
  access_token: string,
  parentSyncId: string,
  subitemSyncId: string,
  boardIds?: string[],
) => {
  try {
    const response = await simbApi.post(
      SUBITEM_UNLINK,
      {
        parentSyncId,
        subitemSyncId,
        ...(boardIds ? { boardIds } : {}),
      },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * Renames a PARENT item's mirrors to one name — the Items tab's "Item name" row.
 *
 * Same shape as `resyncSubitemName`, minus `subitemSyncId`: `namesByBoard` is built from the
 * verdict's `items[]` (`name` and `updated_at` per board), and `sourceBoardId` names the board
 * whose name wins. Served by integration-ms's parent resync service, not utils-ms's
 * `/simb/hard-resync`: that endpoint writes column values, and a name is not a column.
 */
export const resyncParentName = async (
  access_token: string,
  parentSyncId: string,
  namesByBoard: Record<string, { name: string; updatedAt: string }>,
  sourceBoardId?: string,
) => {
  try {
    const response = await simbApi.post(
      SUBITEM_RESYNC_PARENT_NAME,
      {
        parentSyncId,
        namesByBoard,
        ...(sourceBoardId ? { sourceBoardId } : {}),
      },
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};
